import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    HStack,
    IconButton,
    Input,
    Text,
    useColorModeValue,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { MAX_ADMIN_NOTIFICATION_EMAILS } from "models/organization/dto/organization.dto";
import { useEffect, useRef, useState } from "react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useUpdateOrganizationAdminNotificationEmails } from "../hooks/useUpdateOrganizationAdminNotificationEmails";

// Same shape as the API's normalizeAdminNotificationEmails gate. Duplicated
// rather than shared because the DTO files are hand-mirrored across projects —
// this only exists to give the operator an inline error instead of a 400; the
// API re-validates and remains the actual boundary.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/**
 * Explicit recipient list for admin-notification emails (new booking,
 * cancellation, failed payment).
 *
 * Deliberately an explicit list rather than "all admins": operator accounts
 * are email-keyed with open self-signup defaulting to ADMIN, so mailing every
 * admin member would reach every staff account that ever logged in.
 *
 * An empty list is the OFF state — there's no separate toggle, so the channel
 * can never be on with nowhere to send.
 */
export default function AdminNotificationEmailsCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    const [emails, setEmails] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const previousOrgIdRef = useRef<string | undefined>(undefined);

    const { mutate, isLoading } = useUpdateOrganizationAdminNotificationEmails(
        selectedOrganization?.id || '',
        () => {
            toast({
                title: translate('organization.admin_notification_emails_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        },
    );

    // Only re-seed from the server when the selected org actually changes —
    // re-syncing on every render of selectedOrganization would wipe whatever
    // the operator is mid-way through typing.
    useEffect(() => {
        if (previousOrgIdRef.current === selectedOrganization?.id) return;
        previousOrgIdRef.current = selectedOrganization?.id;
        setEmails(selectedOrganization?.adminNotificationEmails ?? []);
        setError(null);
    }, [selectedOrganization?.id, selectedOrganization?.adminNotificationEmails]);

    const handleChange = (index: number, value: string) => {
        setEmails((current) => current.map((email, i) => (i === index ? value : email)));
        setError(null);
    };

    const handleAdd = () => {
        if (emails.length >= MAX_ADMIN_NOTIFICATION_EMAILS) return;
        setEmails((current) => [...current, '']);
    };

    const handleRemove = (index: number) => {
        setEmails((current) => current.filter((_, i) => i !== index));
        setError(null);
    };

    const handleSave = () => {
        if (!selectedOrganization) return;
        if (isLoading) return;

        // Blank rows are dropped, not rejected — the operator may have clicked
        // "add" and then decided against it. The API does the same.
        const cleaned = emails.map((email) => email.trim()).filter(Boolean);

        const invalid = cleaned.find((email) => !EMAIL_PATTERN.test(email));
        if (invalid) {
            setError(translate('organization.admin_notification_emails_invalid').replace('{{email}}', invalid));
            return;
        }

        setError(null);
        mutate(
            { adminNotificationEmails: cleaned },
            {
                onSuccess: (data) => {
                    // Reflect the server's normalized list (lowercased, deduped)
                    // so the editor shows what was actually stored.
                    setEmails(data.adminNotificationEmails);
                },
                onError: () => {
                    toast({
                        title: translate('error'),
                        status: 'error',
                        duration: 3000,
                        position: 'top-right',
                    });
                },
            },
        );
    };

    if (!selectedOrganization) return null;

    return (
        <PageCard title={translate('organization.admin_notification_emails_title')}>
            <Box p={4}>
                <Text fontSize="sm" color="gray.500" mb={4}>
                    {translate('organization.admin_notification_emails_helper')}
                </Text>

                <VStack align="stretch" spacing={2} mb={3}>
                    {emails.length === 0 && (
                        <Text fontSize="sm" color="gray.500" fontStyle="italic">
                            {translate('organization.admin_notification_emails_empty')}
                        </Text>
                    )}
                    {emails.map((email, index) => (
                        <HStack key={index}>
                            <Input
                                type="email"
                                value={email}
                                placeholder="ops@club.com"
                                isDisabled={isLoading}
                                color={textColorPrimary}
                                onChange={(e) => handleChange(index, e.target.value)}
                                aria-label={`${translate('organization.admin_notification_emails_title')} ${index + 1}`}
                            />
                            <IconButton
                                aria-label={translate('delete')}
                                icon={<DeleteIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                isDisabled={isLoading}
                                onClick={() => handleRemove(index)}
                            />
                        </HStack>
                    ))}
                </VStack>

                {error && (
                    <Text fontSize="sm" color="red.500" mb={3}>
                        {error}
                    </Text>
                )}

                <HStack>
                    <Button
                        leftIcon={<AddIcon />}
                        size="sm"
                        variant="outline"
                        onClick={handleAdd}
                        isDisabled={isLoading || emails.length >= MAX_ADMIN_NOTIFICATION_EMAILS}
                    >
                        {translate('organization.admin_notification_emails_add')}
                    </Button>
                    <Button
                        size="sm"
                        colorScheme="brand"
                        onClick={handleSave}
                        isLoading={isLoading}
                    >
                        {translate('save')}
                    </Button>
                </HStack>
            </Box>
        </PageCard>
    );
}
